/*
 * Modern Pink Paper - Matching Product Upsell
 * ------------------------------------------------------------------
 * Shows the customer the matching product (note card <-> notepad) plus
 * the extra-envelopes add-on when they add something to the cart.
 *
 * HARD RULE: this script NEVER adds anything to the cart. Every button
 * is a link to the product page, because these products are
 * personalized and the customer must fill in their options first.
 *
 * Pairings come from Shopify's native "complementary products"
 * metafield, read through /recommendations/products.json?intent=complementary
 * so there is nothing to configure per product.
 *
 * SURVIVES REDIRECTS: some apps (Candy Rack) redirect to the cart page
 * right after an add, which would wipe this popup off the screen. So
 * the add is recorded in sessionStorage and the popup re-opens on the
 * page we land on. It is cleared the moment the shopper interacts with
 * the popup, and 4 seconds after it opens - so it only ever survives an
 * immediate, involuntary redirect, never normal browsing.
 * ------------------------------------------------------------------
 */
(function () {
  'use strict';

  // The loader snippet can be rendered more than once on a page (for
  // example a quickshop form on a collection page). Only run once.
  if (window.__mppMatchingLoaded) return;
  window.__mppMatchingLoaded = true;

  var CFG = window.MPPMatching || {};
  if (CFG.enabled === false) return;

  var ENV_HANDLE = CFG.envelopeHandle || 'envelopes';
  var MONEY_FORMAT = CFG.moneyFormat || '${{amount}}';
  var ROOT = (CFG.rootUrl || '/').replace(/\/$/, '');
  var TEXT = CFG.text || {};

  var HEADING = TEXT.heading || 'Complete the set';
  var SUBHEADING = TEXT.subheading || 'Personalized to match what you just added.';
  var CTA = TEXT.cta || 'Customize & add';
  var NOTE = TEXT.note || 'You will choose your personalization on the next page.';
  var CLOSE = TEXT.close || 'No thanks, keep shopping';
  var BADGE_MATCH = TEXT.badgeMatch || 'Perfect match';
  var BADGE_ENV = TEXT.badgeEnvelope || 'Popular add-on';

  var PENDING_KEY = 'mppPendingAdd';
  var PENDING_MAX_AGE = CFG.redirectGraceMs || 15000;

  var shown = false;
  var busy = false;
  var envCache = null;

  /* ---------------------------------------------------------------- */
  /* pending-add record (lets the popup survive an app redirect)       */
  /* ---------------------------------------------------------------- */

  function setPending(pid) {
    try { sessionStorage.setItem(PENDING_KEY, JSON.stringify({ productId: pid, ts: Date.now() })); } catch (e) {}
  }
  function clearPending() {
    try { sessionStorage.removeItem(PENDING_KEY); } catch (e) {}
  }
  function readPending() {
    try {
      var raw = sessionStorage.getItem(PENDING_KEY);
      return raw ? JSON.parse(raw) : null;
    } catch (e) { return null; }
  }

  /* ---------------------------------------------------------------- */
  /* helpers                                                           */
  /* ---------------------------------------------------------------- */

  function esc(s) {
    return String(s == null ? '' : s)
      .replace(/&/g, '&amp;').replace(/</g, '&lt;').replace(/>/g, '&gt;')
      .replace(/"/g, '&quot;').replace(/'/g, '&#39;');
  }

  function formatMoney(cents) {
    if (typeof cents === 'string') cents = cents.replace('.', '');
    var placeholder = /\{\{\s*(\w+)\s*\}\}/;
    var match = MONEY_FORMAT.match(placeholder);
    if (!match) return MONEY_FORMAT;

    function withDelims(number, precision, thousands, decimal) {
      precision = precision == null ? 2 : precision;
      thousands = thousands == null ? ',' : thousands;
      decimal = decimal == null ? '.' : decimal;
      if (isNaN(number) || number === null) return '0';
      var fixed = (number / 100.0).toFixed(precision);
      var parts = fixed.split('.');
      var whole = parts[0].replace(/(\d)(?=(\d\d\d)+(?!\d))/g, '$1' + thousands);
      return whole + (parts[1] ? decimal + parts[1] : '');
    }

    var value = '0';
    switch (match[1]) {
      case 'amount': value = withDelims(cents, 2); break;
      case 'amount_no_decimals': value = withDelims(cents, 0); break;
      case 'amount_with_comma_separator': value = withDelims(cents, 2, '.', ','); break;
      case 'amount_no_decimals_with_comma_separator': value = withDelims(cents, 0, '.', ','); break;
      case 'amount_with_space_separator': value = withDelims(cents, 2, ' ', ','); break;
      case 'amount_no_decimals_with_space_separator': value = withDelims(cents, 0, ' '); break;
      default: value = withDelims(cents, 2);
    }
    return MONEY_FORMAT.replace(placeholder, value);
  }

  function getJSON(url) {
    return fetch(url, { credentials: 'same-origin', headers: { Accept: 'application/json' } })
      .then(function (r) { return r.ok ? r.json() : null; })
      .catch(function () { return null; });
  }

  function sizedImage(url, width) {
    if (!url) return '';
    return url.replace(/(\.(?:png|jpe?g|gif|webp))(\?|$)/i, '_' + width + 'x$1$2');
  }

  /* ---------------------------------------------------------------- */
  /* data                                                              */
  /* ---------------------------------------------------------------- */

  function getMatches(productId) {
    var url = ROOT + '/recommendations/products.json?product_id=' + encodeURIComponent(productId) +
      '&intent=complementary&limit=' + (CFG.limit || 4);
    return getJSON(url).then(function (d) { return (d && d.products) || []; });
  }

  function getEnvelopes() {
    if (!ENV_HANDLE) return Promise.resolve(null);
    if (envCache !== null) return Promise.resolve(envCache);
    return getJSON(ROOT + '/products/' + ENV_HANDLE + '.js').then(function (p) {
      envCache = p && p.id ? p : false;
      return envCache;
    });
  }

  function getCartProductIds() {
    return getJSON(ROOT + '/cart.js').then(function (c) {
      if (!c || !c.items) return [];
      return c.items.map(function (i) { return Number(i.product_id); });
    });
  }

  /* ---------------------------------------------------------------- */
  /* rendering                                                         */
  /* ---------------------------------------------------------------- */

  function cardHTML(p, badge) {
    var raw = (p.featured_image && (p.featured_image.url || p.featured_image)) || p.image;
    var img = sizedImage(raw, 300);
    var price = formatMoney(p.price);
    var varies = p.price_varies || (p.price_min != null && p.price_max != null && p.price_min !== p.price_max);
    var href = p.url || (ROOT + '/products/' + p.handle);

    return '' +
      '<a class="mpp-card" href="' + esc(href) + '">' +
        '<span class="mpp-card__media">' +
          (img ? '<img src="' + esc(img) + '" alt="' + esc(p.title) + '" loading="lazy">' : '') +
        '</span>' +
        '<span class="mpp-card__body">' +
          (badge ? '<span class="mpp-card__badge">' + esc(badge) + '</span>' : '') +
          '<span class="mpp-card__title">' + esc(p.title) + '</span>' +
          '<span class="mpp-card__price">' + (varies ? 'From ' : '') + esc(price) + '</span>' +
          '<span class="mpp-card__cta">' + esc(CTA) + ' <span aria-hidden="true">&rarr;</span></span>' +
        '</span>' +
      '</a>';
  }

  function closeModal() {
    var el = document.getElementById('mpp-modal');
    if (!el) return;
    el.classList.remove('is-open');
    document.documentElement.classList.remove('mpp-locked');
    setTimeout(function () { if (el.parentNode) el.parentNode.removeChild(el); }, 200);
    document.removeEventListener('keydown', onKeydown);
  }

  function onKeydown(e) {
    if (e.key === 'Escape' || e.keyCode === 27) { clearPending(); closeModal(); }
  }

  function openModal(items) {
    closeModal();

    var wrap = document.createElement('div');
    wrap.id = 'mpp-modal';
    wrap.className = 'mpp-modal';
    wrap.setAttribute('role', 'dialog');
    wrap.setAttribute('aria-modal', 'true');
    wrap.setAttribute('aria-labelledby', 'mpp-modal-title');

    wrap.innerHTML = '' +
      '<div class="mpp-modal__backdrop" data-mpp-close></div>' +
      '<div class="mpp-modal__panel">' +
        '<button type="button" class="mpp-modal__x" data-mpp-close aria-label="Close">&times;</button>' +
        '<p class="mpp-modal__added">Added to your cart</p>' +
        '<h2 class="mpp-modal__title" id="mpp-modal-title">' + esc(HEADING) + '</h2>' +
        '<p class="mpp-modal__sub">' + esc(SUBHEADING) + '</p>' +
        '<div class="mpp-modal__cards">' +
          items.map(function (it) { return cardHTML(it.product, it.badge); }).join('') +
        '</div>' +
        '<p class="mpp-modal__note">' + esc(NOTE) + '</p>' +
        '<div class="mpp-modal__actions">' +
          '<button type="button" class="mpp-modal__continue" data-mpp-close>' + esc(CLOSE) + '</button>' +
          '<a class="mpp-modal__cart" href="' + ROOT + '/cart">View cart</a>' +
        '</div>' +
      '</div>';

    document.body.appendChild(wrap);
    document.documentElement.classList.add('mpp-locked');

    wrap.addEventListener('click', function (e) {
      // Any deliberate interaction means the shopper has seen the offer,
      // so the popup must not follow them to the next page.
      if (e.target.closest('[data-mpp-close]')) {
        e.preventDefault();
        clearPending();
        closeModal();
        return;
      }
      if (e.target.closest('a')) clearPending();
    });
    document.addEventListener('keydown', onKeydown);

    // If the page is still here 4 seconds later, no app is redirecting
    // us, so drop the record and never re-open on a later page.
    setTimeout(clearPending, 4000);

    // Reveal. requestAnimationFrame gives the nice fade-in, but it can be
    // throttled (background tabs, some extensions), and the popup must
    // never be left invisible - so a timer backs it up.
    var reveal = function () { wrap.classList.add('is-open'); };
    if (window.requestAnimationFrame) window.requestAnimationFrame(reveal);
    setTimeout(reveal, 60);

    var x = wrap.querySelector('.mpp-modal__x');
    if (x) x.focus();
  }

  /* ---------------------------------------------------------------- */
  /* orchestration                                                     */
  /* ---------------------------------------------------------------- */

  function showFor(productId) {
    if (busy || shown) return;
    busy = true;
    productId = Number(productId);

    Promise.all([getMatches(productId), getEnvelopes(), getCartProductIds()])
      .then(function (res) {
        var matches = res[0] || [];
        var env = res[1];
        var inCart = res[2] || [];
        var items = [];
        var seen = {};
        seen[productId] = true;

        matches.forEach(function (p) {
          if (seen[p.id]) return;
          if (inCart.indexOf(Number(p.id)) !== -1) return;
          seen[p.id] = true;
          items.push({ product: p, badge: BADGE_MATCH });
        });

        if (env && env.id && !seen[env.id]) {
          var envInCart = inCart.indexOf(Number(env.id)) !== -1;
          if (!envInCart || CFG.alwaysShowEnvelopes) {
            seen[env.id] = true;
            items.push({ product: env, badge: BADGE_ENV });
          }
        }

        busy = false;
        if (!items.length) { clearPending(); return; }
        shown = true;
        setTimeout(function () { shown = false; }, 1500);
        openModal(items);
      })
      .catch(function () { busy = false; });
  }

  function handleAddResponse(data) {
    var pid = null;
    if (data) {
      if (data.product_id) pid = data.product_id;
      else if (data.items && data.items.length && data.items[0].product_id) pid = data.items[0].product_id;
    }
    if (pid) { setPending(pid); showFor(pid); return; }
    // Could not read the response - fall back to the newest cart line.
    getJSON(ROOT + '/cart.js').then(function (c) {
      if (c && c.items && c.items.length) {
        var last = c.items[c.items.length - 1].product_id;
        setPending(last);
        showFor(last);
      }
    });
  }

  function isAddUrl(url) {
    if (!url) return false;
    try { return String(url).indexOf('/cart/add') !== -1; } catch (e) { return false; }
  }

  /* --- intercept XHR (this theme's ajax cart uses jQuery) ---------- */
  var _open = XMLHttpRequest.prototype.open;
  var _send = XMLHttpRequest.prototype.send;

  XMLHttpRequest.prototype.open = function (method, url) {
    this.__mppAdd = isAddUrl(url) && /post/i.test(String(method || ''));
    return _open.apply(this, arguments);
  };

  XMLHttpRequest.prototype.send = function () {
    var xhr = this;
    if (xhr.__mppAdd) {
      xhr.addEventListener('load', function () {
        if (xhr.status < 200 || xhr.status >= 300) return;
        var data = null;
        try { data = JSON.parse(xhr.responseText); } catch (e) { data = null; }
        handleAddResponse(data);
      });
    }
    return _send.apply(this, arguments);
  };

  /* --- intercept fetch (apps / newer code) ------------------------- */
  if (window.fetch) {
    var _fetch = window.fetch;
    window.fetch = function (input, init) {
      var url = (typeof input === 'string') ? input : (input && input.url);
      var method = (init && init.method) || (input && input.method) || 'GET';
      var watching = isAddUrl(url) && /post/i.test(String(method));
      var p = _fetch.apply(this, arguments);
      if (watching) {
        p.then(function (res) {
          if (!res || !res.ok) return;
          res.clone().json().then(handleAddResponse).catch(function () { handleAddResponse(null); });
        }).catch(function () {});
      }
      return p;
    };
  }

  /* --- fallback for non-ajax (full page reload) adds ---------------- */
  document.addEventListener('submit', function (e) {
    var form = e.target;
    if (!form || !form.action || !isAddUrl(form.action)) return;
    var holder = form.closest('[data-product-id]');
    var pid = form.getAttribute('data-product-id') || (holder && holder.getAttribute('data-product-id'));
    if (pid) setPending(pid);
  }, true);

  /* --- on load: re-open if an app redirected us mid-popup ----------- */
  function checkPending() {
    var data = readPending();
    if (!data) return;
    clearPending();                         // show at most once per add
    var age = Date.now() - (data.ts || 0);
    if (age > PENDING_MAX_AGE) return;      // stale, ignore
    if (data.productId) { showFor(data.productId); return; }
    getJSON(ROOT + '/cart.js').then(function (c) {
      if (c && c.items && c.items.length) showFor(c.items[c.items.length - 1].product_id);
    });
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', checkPending);
  } else {
    checkPending();
  }

  /* --- manual trigger, handy for testing --------------------------- */
  window.MPPMatching = window.MPPMatching || {};
  window.MPPMatching.showFor = showFor;
})();